import {
  FormatType,
  GaugeChartV2Tooltip,
  type GaugeChartV2TooltipRender,
  getFormatter,
} from '@revelio/replots';

import type { MultiMetricDataMap } from '../../DataProvider/MultiMetricProvider';
import type { DataProviderSet, SingleDataSet } from '../../DataProvider/types';
import { extractMetricsFromText } from '../../DataProvider/utils/text-metrics';
import type { MetricValues } from '../../PageConfigProvider/types/metric';
import { extractDatasetValues } from '../TextChart/utils/extractDatasetValues';

type TooltipRowSource = {
  label: string;
  metric: MetricValues;
  format: (value: number) => string;
};

const formatCount = getFormatter(FormatType.INTEGER);
const formatPercent = getFormatter(FormatType.PERCENTAGE);
const formatGrowth = getFormatter('.1%');
const formatDays = getFormatter(FormatType.DAY);

const GAUGE_TOOLTIP_ROWS: Partial<Record<MetricValues, TooltipRowSource[]>> = {
  demand_supply_percentile: [
    {
      label: 'Demand',
      metric: 'postings_active',
      format: (value) => `${formatCount(value)} postings`,
    },
    {
      label: 'Supply',
      metric: 'headcount',
      format: (value) => `${formatCount(value)} profiles`,
    },
  ],
  wage_pressure_percentile: [
    {
      label: 'YoY change',
      metric: 'wage_pressure',
      format: (value) => `${value > 0 ? '+' : ''}${formatGrowth(value)}`,
    },
  ],
  time_to_fill_percentile: [
    { label: 'Time to fill', metric: 'time_to_fill', format: formatDays },
  ],
};

export const getGaugeTooltipMetrics = (valueTemplate: string): MetricValues[] =>
  extractMetricsFromText(valueTemplate).flatMap(
    (metric) => GAUGE_TOOLTIP_ROWS[metric]?.map((row) => row.metric) ?? []
  );

const getEntityValue = (
  dataset: DataProviderSet,
  { id, name }: { id?: string; name?: string }
): number | null => {
  const byId = id ? extractDatasetValues(dataset, { entity: id }) : [];
  const values =
    byId.length === 0 && name
      ? extractDatasetValues(dataset, { entity: name })
      : byId;
  return values.filter((value) => value !== null).at(-1) ?? null;
};

export const buildGaugeV2Tooltip = ({
  valueTemplate,
  multiData,
  entityValues,
}: {
  valueTemplate: string;
  multiData?: MultiMetricDataMap;
  entityValues: SingleDataSet['data'];
}): GaugeChartV2TooltipRender => {
  const rowSources = extractMetricsFromText(valueTemplate).flatMap(
    (metric) => GAUGE_TOOLTIP_ROWS[metric] ?? []
  );
  const rows = rowSources.flatMap(({ metric, ...row }) => {
    const dataset = multiData?.[metric];
    return dataset ? [{ ...row, dataset }] : [];
  });
  const hasRows = rows.length > 0 && rows.length === rowSources.length;

  return ({ id, label, value }) => {
    const name =
      label ??
      (id ? entityValues.find((entry) => entry.id === id)?.dim : undefined);
    if (!hasRows) {
      return <GaugeChartV2Tooltip title={name} value={formatPercent(value)} />;
    }
    return (
      <GaugeChartV2Tooltip
        title={name}
        rows={rows.map(({ label: rowLabel, format, dataset }) => {
          const rowValue = getEntityValue(dataset, { id, name });
          return {
            label: rowLabel,
            value: rowValue === null ? '—' : format(rowValue),
          };
        })}
      />
    );
  };
};
